import React, { useState, useEffect, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  doc,
  addDoc,
  updateDoc,
  deleteDoc, // AJOUTÉ
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import {
  Box,
  Package,
  History,
  Plus,
  Minus,
  AlertTriangle,
  CheckCircle,
  Save,
  X,
  Loader2,
  List,
  Edit,
  DollarSign,
  Trash, // AJOUTÉ
  Search, // AJOUTÉ
  Filter, // AJOUTÉ
} from 'lucide-react';

// ===============================================
// Fonctions utilitaires
// ===============================================

// Fonction utilitaire pour formater les devises
const formatCurrency = (value) => {
  if (typeof value !== 'number') return 'N/A';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF', // CFA franc
    minimumFractionDigits: 0,
  }).format(value);
};

// Composant FlashMessage
const FlashMessage = ({ type, text }) => (
  <div className={`p-3 mb-4 rounded-lg flex items-center shadow-md transition-all duration-300 ${
      type === 'error'
      ? 'bg-red-100 border border-red-400 text-red-700'
      : 'bg-green-100 border border-green-400 text-green-700'
  }`}>
      {type === 'error' ? <AlertTriangle size={20} className="mr-2" /> : <CheckCircle size={20} className="mr-2" />}
      <span>{text}</span>
  </div>
);

// ===============================================
// Composants Enfants
// ===============================================

// Composant de liste des stocks (MIS À JOUR : Filtres et Suppression)
const StockList = ({ 
  consumables, 
  editingItem, 
  startEdit, 
  handleSaveEdit, 
  setEditingItem, 
  formatCurrency,
  handleDeleteItem, // NOUVEAU
  stockSearchTerm, // NOUVEAU
  setStockSearchTerm, // NOUVEAU
  stockFilterStatus, // NOUVEAU
  setStockFilterStatus, // NOUVEAU
}) => (
  <div className="space-y-4">
      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 mb-6 p-4 bg-gray-50 rounded-xl shadow-inner">
          {/* Filtre 1: Recherche par nom */}
          <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                  type="text"
                  placeholder="Rechercher par nom d'article..."
                  value={stockSearchTerm}
                  onChange={(e) => setStockSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
          </div>
          {/* Filtre 2: Statut de Réapprovisionnement */}
          <div className="relative">
              <Filter size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                  value={stockFilterStatus}
                  onChange={(e) => setStockFilterStatus(e.target.value)}
                  className="w-full md:w-auto appearance-none pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              >
                  <option value="all">Tous les Statuts</option>
                  <option value="alert">À Réapprovisionner</option>
                  <option value="ok">Stock OK</option>
              </select>
          </div>
      </div>

      {consumables.length === 0 ? (
          <p className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg">
              {stockSearchTerm || stockFilterStatus !== 'all' ? "Aucun article ne correspond à ces filtres." : "Aucun consommable enregistré. Utilisez l'onglet 'Ajouter un article' pour commencer."}
          </p>
      ) : (
          <div className="overflow-x-auto shadow-lg rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-indigo-50">
                      <tr>
                          <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Article</th>
                          <th className="px-6 py-3 text-right text-xs font-bold text-indigo-700 uppercase tracking-wider">Prix Unitaire</th>
                          <th className="px-6 py-3 text-right text-xs font-bold text-indigo-700 uppercase tracking-wider">Stock Actuel</th>
                          <th className="px-6 py-3 text-right text-xs font-bold text-indigo-700 uppercase tracking-wider">Valeur Totale</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Seuil de Réappro.</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Statut</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Actions</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Supprimer</th> {/* NOUVELLE COLONNE */}
                      </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                      {consumables.map(item => {
                          const needsReorder = item.currentStock <= item.reorderThreshold;
                          const totalValue = item.currentStock * item.unitPrice;
                          const isEditing = editingItem?.id === item.id;

                          if (isEditing) {
                              // ... (Row for editing remains the same)
                              return (
                                  <tr key={item.id} className="bg-yellow-50">
                                      <td className="p-6 whitespace-nowrap text-sm font-medium text-gray-900">
                                          <input
                                              type="text"
                                              value={editingItem.name}
                                              onChange={(e) => setEditingItem(prev => ({ ...prev, name: e.target.value }))}
                                              className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 p-2"
                                          />
                                      </td>
                                      <td className="p-6 whitespace-nowrap text-sm text-gray-500 text-right">
                                          <input
                                              type="number"
                                              step="0.01"
                                              value={editingItem.unitPrice}
                                              onChange={(e) => setEditingItem(prev => ({ ...prev, unitPrice: parseFloat(e.target.value) || 0 }))}
                                              className="w-24 text-right border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 p-2"
                                          />
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                          {item.currentStock} {item.unit}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                                          {formatCurrency(totalValue)}
                                      </td>
                                      <td className="p-6 whitespace-nowrap text-sm text-gray-500">
                                          <input
                                              type="number"
                                              value={editingItem.reorderThreshold}
                                              onChange={(e) => setEditingItem(prev => ({ ...prev, reorderThreshold: parseInt(e.target.value) || 0 }))}
                                              className="w-24 border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 p-2"
                                          />
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                          <span className="text-xs text-gray-500">
                                              En édition...
                                          </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                          <button
                                              onClick={handleSaveEdit}
                                              className="text-indigo-600 hover:text-indigo-900 mr-3"
                                              title="Sauvegarder"
                                          >
                                              <Save size={18} />
                                          </button>
                                          <button
                                              onClick={() => setEditingItem(null)}
                                              className="text-red-600 hover:text-red-900"
                                              title="Annuler"
                                          >
                                              <X size={18} />
                                          </button>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                          N/A
                                      </td>
                                  </tr>
                              );
                          }

                          return (
                              <tr key={item.id} className={needsReorder ? 'bg-red-50 hover:bg-red-100' : 'even:bg-gray-50 hover:bg-gray-100'}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.name} ({item.unit})</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(item.unitPrice)}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">{item.currentStock} {item.unit}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-indigo-700 text-right">{formatCurrency(totalValue)}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.reorderThreshold} {item.unit}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                      {needsReorder ? (
                                          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                              <AlertTriangle size={14} className="mr-1" /> À Réapprovisionner
                                          </span>
                                      ) : (
                                          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                              <CheckCircle size={14} className="mr-1" /> Stock OK
                                          </span>
                                      )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                      <button
                                          onClick={() => startEdit(item)}
                                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                                          title="Modifier l'article"
                                      >
                                          <Edit size={18} />
                                      </button>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                      <button
                                          onClick={() => handleDeleteItem(item)} // APPEL DE LA FONCTION DE SUPPRESSION
                                          className="text-red-600 hover:text-red-900"
                                          title="Supprimer l'article"
                                      >
                                          <Trash size={18} />
                                      </button>
                                  </td>
                              </tr>
                          );
                      })}
                  </tbody>
              </table>
          </div>
      )}
  </div>
);

// Composant pour l'ajout d'un nouvel article (inchangé)
const AddItemForm = ({ newItem, setNewItem, handleAddItem }) => (
  <form onSubmit={handleAddItem} className="bg-white p-6 rounded-xl shadow-lg space-y-6 max-w-lg mx-auto">
      <h3 className="text-xl font-semibold text-gray-800 flex items-center mb-4">
          <Plus size={24} className="mr-2 text-indigo-600" /> Ajouter un Nouveau Consommable
      </h3>

      <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nom de l'article <span className="text-red-500">*</span></label>
          <input
              type="text"
              id="name"
              value={newItem.name}
              onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Ex: Stylos Bleus, Feuilles A4, Craie Blanche"
              required
          />
      </div>

      <div>
          <label htmlFor="unit" className="block text-sm font-medium text-gray-700">Unité de mesure <span className="text-red-500">*</span></label>
          <input
              type="text"
              id="unit"
              value={newItem.unit}
              onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Ex: unité, boîte, paquet, carton"
              required
          />
      </div>

      <div>
          <label htmlFor="unitPrice" className="block text-sm font-medium text-gray-700">Prix Unitaire de Référence</label>
          <p className="text-xs text-gray-500 mb-1">Coût par unité pour le calcul de la valeur du stock.</p>
          <div className="relative mt-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign size={16} className="text-gray-400" />
              </div>
              <input
                  type="number"
                  step="0.01"
                  id="unitPrice"
                  value={newItem.unitPrice}
                  onChange={(e) => setNewItem(prev => ({ ...prev, unitPrice: parseFloat(e.target.value) || 0 }))}
                  className="block w-full border border-gray-300 rounded-lg shadow-sm p-3 pl-10 focus:ring-indigo-500 focus:border-indigo-500 text-right"
                  min="0"
              />
          </div>
      </div>

      <div>
          <label htmlFor="threshold" className="block text-sm font-medium text-gray-700">Seuil de Réapprovisionnement</label>
          <p className="text-xs text-gray-500 mb-1">Alerte stock lorsque la quantité atteint ou descend en dessous de ce nombre.</p>
          <input
              type="number"
              id="threshold"
              value={newItem.reorderThreshold}
              onChange={(e) => setNewItem(prev => ({ ...prev, reorderThreshold: parseInt(e.target.value) || 0 }))}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-indigo-500 focus:border-indigo-500"
              min="0"
          />
      </div>
      
      {/* CHAMP : QUANTITÉ INITIALE */}
      <div>
          <label htmlFor="initialStock" className="block text-sm font-medium text-gray-700">Quantité en stock initiale</label>
          <p className="text-xs text-gray-500 mb-1">Stock de départ lors de l'ajout de cet article.</p>
          <input
              type="number"
              id="initialStock"
              value={newItem.initialStock}
              onChange={(e) => setNewItem(prev => ({ ...prev, initialStock: parseInt(e.target.value) || 0 }))}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-indigo-500 focus:border-indigo-500"
              min="0"
          />
      </div>

      <button
          type="submit"
          className="w-full flex justify-center items-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg shadow-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
      >
          <Save size={20} className="mr-2" /> Enregistrer l'article
      </button>
  </form>
);

// Composant pour l'enregistrement des mouvements de stock (inchangé)
const StockMoveForm = ({ consumables, stockMove, setStockMove, handleStockMove, formatCurrency, displayMessage }) => (
  <form onSubmit={handleStockMove} className="bg-white p-6 rounded-xl shadow-lg space-y-6 max-w-lg mx-auto">
       <h3 className="text-xl font-semibold text-gray-800 flex items-center mb-4">
          <List size={24} className="mr-2 text-indigo-600" /> Enregistrer un Mouvement de Stock
      </h3>

      {consumables.length === 0 && (
          <div className="p-3 bg-yellow-100 text-yellow-800 rounded-lg">
              Veuillez d'abord ajouter des articles dans l'onglet "Ajouter un article".
          </div>
      )}

      <div>
          <label htmlFor="item" className="block text-sm font-medium text-gray-700">Article concerné <span className="text-red-500">*</span></label>
          <select
              id="item"
              value={stockMove.itemId}
              onChange={(e) => {
                  const item = consumables.find(c => c.id === e.target.value);
                  setStockMove(prev => ({
                      ...prev,
                      itemId: e.target.value,
                      // Pré-remplir le prix d'achat avec le prix unitaire de référence de l'article
                      purchasePrice: item ? item.unitPrice : 0.00
                  }))
              }}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-indigo-500 focus:border-indigo-500"
              required
              disabled={consumables.length === 0}
          >
              <option value="">-- Sélectionner un article --</option>
              {consumables.map(item => (
                  <option key={item.id} value={item.id}>
                      {item.name} (Stock: {item.currentStock} {item.unit}) - {formatCurrency(item.unitPrice)}
                  </option>
              ))}
          </select>
      </div>

      <div className="flex space-x-4">
          <div className="flex-1">
              <label htmlFor="type" className="block text-sm font-medium text-gray-700">Type de Mouvement</label>
              <div className="mt-1 flex space-x-2">
                  <button
                      type="button"
                      onClick={() => setStockMove(prev => ({ ...prev, type: 'consumption' }))}
                      className={`flex-1 flex items-center justify-center p-3 rounded-lg border transition duration-150 ${
                          stockMove.type === 'consumption'
                          ? 'bg-red-500 text-white border-red-600 shadow-md'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                      <Minus size={20} className="mr-2" /> Sortie / Consommation
                  </button>
                  <button
                      type="button"
                      onClick={() => setStockMove(prev => ({ ...prev, type: 'restock' }))}
                      className={`flex-1 flex items-center justify-center p-3 rounded-lg border transition duration-150 ${
                          stockMove.type === 'restock'
                          ? 'bg-green-500 text-white border-green-600 shadow-md'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                      <Plus size={20} className="mr-2" /> Entrée / Réapprovisionnement
                  </button>
              </div>
          </div>
      </div>

      {stockMove.type === 'restock' && (
          <div>
              <label htmlFor="purchasePrice" className="block text-sm font-medium text-gray-700">Prix d'Achat Unitaire (de cette commande)</label>
              <p className="text-xs text-gray-500 mb-1">Utilisé pour calculer le coût total du réapprovisionnement.</p>
              <div className="relative mt-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign size={16} className="text-gray-400" />
                  </div>
                  <input
                      type="number"
                      step="0.01"
                      id="purchasePrice"
                      value={stockMove.purchasePrice}
                      onChange={(e) => setStockMove(prev => ({ ...prev, purchasePrice: parseFloat(e.target.value) || 0 }))}
                      className="block w-full border border-gray-300 rounded-lg shadow-sm p-3 pl-10 focus:ring-indigo-500 focus:border-indigo-500 text-right"
                      min="0"
                  />
              </div>
          </div>
      )}


      <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">Quantité <span className="text-red-500">*</span></label>
          <input
              type="number"
              id="quantity"
              value={stockMove.quantity}
              onChange={(e) => setStockMove(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-indigo-500 focus:border-indigo-500"
              min="1"
              required
          />
      </div>

      <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notes (Optionnel)</label>
          <textarea
              id="notes"
              value={stockMove.notes}
              onChange={(e) => setStockMove(prev => ({ ...prev, notes: e.target.value }))}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-indigo-500 focus:border-indigo-500"
              rows="2"
              placeholder="Ex: Distribution aux classes de Terminale / Commande fournisseur X"
          />
      </div>

      <button
          type="submit"
          className={`w-full flex justify-center items-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg shadow-md text-white transition duration-150 ease-in-out ${
              stockMove.type === 'consumption'
              ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
              : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
          }`}
          disabled={consumables.length === 0}
      >
          <Save size={20} className="mr-2" /> Confirmer le Mouvement de Stock
      </button>
  </form>
);

// Composant d'affichage de l'historique (MIS À JOUR : Filtres)
const HistoryTable = ({ 
    records, 
    formatCurrency, 
    consumables, // NOUVEAU
    historyFilterItem, // NOUVEAU
    setHistoryFilterItem, // NOUVEAU
    historyFilterType, // NOUVEAU
    setHistoryFilterType // NOUVEAU
}) => (
  <div className="space-y-4">
      <h3 className="text-xl font-semibold text-gray-800 flex items-center mb-4">
          <History size={24} className="mr-2 text-indigo-600" /> Historique des 100 Derniers Mouvements
      </h3>
      
      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 mb-6 p-4 bg-gray-50 rounded-xl shadow-inner">
          {/* Filtre 1: Article */}
          <div className="relative flex-1">
              <Box size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                  value={historyFilterItem}
                  onChange={(e) => setHistoryFilterItem(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              >
                  <option value="all">Tous les Articles</option>
                  {consumables.map(item => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
              </select>
          </div>
          {/* Filtre 2: Type de Mouvement */}
          <div className="relative">
              <Filter size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                  value={historyFilterType}
                  onChange={(e) => setHistoryFilterType(e.target.value)}
                  className="w-full md:w-auto appearance-none pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              >
                  <option value="all">Tous les Types</option>
                  <option value="restock">Entrée / Réapprovisionnement</option>
                  <option value="consumption">Sortie / Consommation</option>
              </select>
          </div>
      </div>

      {records.length === 0 ? (
          <p className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg">
              {historyFilterItem !== 'all' || historyFilterType !== 'all' ? "Aucun mouvement ne correspond à ces filtres." : "Aucun mouvement de stock enregistré."}
          </p>
      ) : (
          <div className="overflow-x-auto shadow-lg rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-indigo-50">
                      <tr>
                          <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Date/Heure</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Article</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-right text-xs font-bold text-indigo-700 uppercase tracking-wider">Quantité</th>
                          <th className="px-6 py-3 text-right text-xs font-bold text-indigo-700 uppercase tracking-wider">Coût Unitaire</th>
                          <th className="px-6 py-3 text-right text-xs font-bold text-indigo-700 uppercase tracking-wider">Coût Total</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Notes</th>
                      </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                      {records.map((record) => (
                          <tr key={record.id} className="even:bg-gray-50 hover:bg-gray-100">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.timestamp}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.itemName || 'Article inconnu'}</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium ${
                                      record.type === 'consumption'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                      {record.type === 'consumption' ? 'Sortie' : 'Entrée'}
                                  </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-right text-gray-900">
                                  {record.type === 'consumption' ? '-' : '+'}{record.quantity}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                  {formatCurrency(record.purchasePrice)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-right text-indigo-700">
                                  {formatCurrency(record.totalCost)}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{record.notes || 'N/A'}</td>
                          </tr>
                      ))}
                  </tbody>
              </table>
          </div>
      )}
  </div>
);

// ===============================================
// Composant Principal ConsumablesManagement
// ===============================================

const ConsumablesManagement = ({
  db,
  schoolId,
  currentUserId,
  userRole,
  error,
  message,
  setError,
  setMessage,
  isAuthReady,
}) => {
  // --- États ---
  const [consumables, setConsumables] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stock');
  
  // NOUVEAUX ÉTATS POUR LES FILTRES DE STOCK
  const [stockSearchTerm, setStockSearchTerm] = useState('');
  const [stockFilterStatus, setStockFilterStatus] = useState('all'); // 'all', 'alert', 'ok'

  // NOUVEAUX ÉTATS POUR LES FILTRES D'HISTORIQUE
  const [historyFilterItem, setHistoryFilterItem] = useState('all'); // itemId for history filter
  const [historyFilterType, setHistoryFilterType] = useState('all'); // 'all', 'consumption', 'restock'
  
  const [newItem, setNewItem] = useState({
    name: '',
    unit: '',
    reorderThreshold: 10,
    unitPrice: 0.0,
    initialStock: 0, 
  });
  
  const [stockMove, setStockMove] = useState({
    itemId: '',
    quantity: 1,
    type: 'consumption',
    notes: '',
    purchasePrice: 0.0,
  });
  const [editingItem, setEditingItem] = useState(null);

  // --- Références Firestore ---
  const CONSUMABLES_PATH = `schools/${schoolId}/consumables`;
  const RECORDS_PATH = `schools/${schoolId}/consumptionRecords`;

  // --- Chargement des données (inchangé) ---
  useEffect(() => {
    if (!db || !schoolId || !currentUserId || !isAuthReady) {
      setLoading(false);
      setError("Connexion Firebase ou identifiants utilisateur manquants.");
      return;
    }

    const consumablesQuery = collection(db, CONSUMABLES_PATH);
    const recordsQuery = query(
      collection(db, RECORDS_PATH),
      orderBy('timestamp', 'desc'),
      limit(100)
    );

    const unsubscribeConsumables = onSnapshot(
      consumablesQuery,
      (snapshot) => {
        const items = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          unitPrice: parseFloat(doc.data().unitPrice) || 0,
          reorderThreshold: parseInt(doc.data().reorderThreshold) || 0,
          currentStock: parseInt(doc.data().currentStock) || 0,
        }));
        setConsumables(items);
        setLoading(false);
      },
      (err) => {
        console.error('Erreur de snapshot consommables:', err);
        setError('Impossible de charger les stocks.');
        setLoading(false);
      }
    );
    const unsubscribeRecords = onSnapshot(recordsQuery, (snapshot) => {
      const history = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate().toLocaleString('fr-FR'),
        quantity: parseInt(doc.data().quantity) || 0,
        purchasePrice: parseFloat(doc.data().purchasePrice) || 0,
        totalCost: parseFloat(doc.data().totalCost) || 0,
      }));
      setRecords(history);
    });
    return () => {
      unsubscribeConsumables();
      unsubscribeRecords();
    };
  }, [db, schoolId, currentUserId, isAuthReady, setError, CONSUMABLES_PATH, RECORDS_PATH]);

  // --- Message rapide (inchangé) ---
  const displayMessage = useCallback(
    (msg, isError = false) => {
      let timer;
      if (isError) {
        setError(msg);
        timer = setTimeout(() => setError(null), 4000);
      } else {
        setMessage(msg);
        timer = setTimeout(() => setMessage(null), 3000);
      }
      return () => clearTimeout(timer);
    },
    [setError, setMessage]
  );

  // --- handleAddItem (inchangé) ---
  const handleAddItem = useCallback(async (e) => {
    e.preventDefault();
    const { name, unit, reorderThreshold, unitPrice, initialStock } = newItem;

    if (!db || !schoolId || !currentUserId || !name || !unit) {
      return displayMessage(
        'Veuillez remplir tous les champs obligatoires.',
        true
      );
    }

    try {
      await addDoc(collection(db, CONSUMABLES_PATH), {
        name: name.trim(),
        unit: unit.trim(),
        reorderThreshold: Math.max(0, parseInt(reorderThreshold) || 0),
        unitPrice: parseFloat(unitPrice) || 0,
        currentStock: Math.max(0, parseInt(initialStock) || 0), 
        lastUpdated: new Date(),
        createdBy: currentUserId,
        schoolId : schoolId
      });
      setNewItem({ name: '', unit: '', reorderThreshold: 10, unitPrice: 0.0, initialStock: 0 });
      displayMessage('Consommable ajouté avec succès.');
    } catch (err) {
      console.error(err);
      displayMessage('Erreur lors de l’ajout du consommable.', true);
    }
  }, [db, schoolId, currentUserId, newItem, displayMessage, CONSUMABLES_PATH]);

  // --- handleStockMove (inchangé) ---
  const handleStockMove = useCallback(async (e) => {
      e.preventDefault();
      const { itemId, quantity, type, notes, purchasePrice } = stockMove;

      if (!db || !currentUserId || !itemId || quantity <= 0) {
          return displayMessage("Veuillez sélectionner un article et entrer une quantité valide.", true);
      }

      const itemRef = doc(db, CONSUMABLES_PATH, itemId);
      const stockChange = type === 'consumption' ? -quantity : quantity;
      
      const item = consumables.find(c => c.id === itemId);
      if (!item) {
          return displayMessage("Article non trouvé.", true);
      }

      let recordedUnitPrice;
      if (type === 'restock') {
          recordedUnitPrice = parseFloat(purchasePrice) || 0;
      } else {
          recordedUnitPrice = item.unitPrice; 
      }
      const totalCost = recordedUnitPrice * quantity; 

      try {
          const newStock = item.currentStock + stockChange;
          if (newStock < 0) {
              return displayMessage(`Stock insuffisant. Seulement ${item.currentStock} ${item.unit} disponibles.`, true);
          }

          // 1. Mise à jour du stock
          const updateData = {
              currentStock: newStock,
              lastUpdated: new Date(),
          };
          if (type === 'restock' && recordedUnitPrice > 0) {
              updateData.unitPrice = recordedUnitPrice;
          }

          await updateDoc(itemRef, updateData);

          // 2. Enregistrement du mouvement
          await addDoc(collection(db, RECORDS_PATH), {
              itemId: itemId,
              itemName: item.name,
              type: type,
              quantity: quantity,
              userId: currentUserId,
              timestamp: new Date(),
              notes: notes,
              schoolId: schoolId,
              purchasePrice: recordedUnitPrice, 
              totalCost: totalCost,
          });
          displayMessage(`Stock mis à jour (${type === 'consumption' ? 'Sortie' : 'Entrée'} de ${quantity} ${item.unit}).`);
          setStockMove({ itemId: '', quantity: 1, type: 'consumption', notes: '', purchasePrice: 0.00 });
      } catch (e) {
          console.error("Erreur lors du mouvement de stock:", e);
          displayMessage("Erreur lors de la mise à jour du stock. Veuillez réessayer.", true);
      }
  }, [db, currentUserId, stockMove, consumables, displayMessage, CONSUMABLES_PATH, RECORDS_PATH, schoolId]);


  // --- NOUVELLE FONCTION : handleDeleteItem ---
  const handleDeleteItem = useCallback(async (item) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'article "${item.name}"? ⚠️ Cette action est irréversible et NE SUPPRIME PAS l'historique associé.`)) {
      return;
    }

    if (!db || !item.id) {
      return displayMessage("Erreur: ID de l'article manquant.", true);
    }

    try {
      await deleteDoc(doc(db, CONSUMABLES_PATH, item.id));
      displayMessage(`Article "${item.name}" supprimé avec succès.`, false);
    } catch (e) {
      console.error("Erreur lors de la suppression:", e);
      displayMessage("Erreur lors de la suppression. Veuillez réessayer.", true);
    }
  }, [db, displayMessage, CONSUMABLES_PATH]);
  // ----------------------------------------------


  // Entrée en mode édition (inchangé)
  const startEdit = useCallback((item) => {
      setEditingItem({
          id: item.id,
          name: item.name,
          unit: item.unit,
          reorderThreshold: item.reorderThreshold,
          unitPrice: item.unitPrice,
      });
  }, []);

  // Sauvegarde de l'édition (inchangé)
  const handleSaveEdit = useCallback(async (e) => {
      e.preventDefault();
      if (!db || !editingItem || !editingItem.id || !editingItem.name || !editingItem.unit) {
          return displayMessage("Veuillez remplir tous les champs obligatoires.", true);
      }

      const itemRef = doc(db, CONSUMABLES_PATH, editingItem.id);
      const price = Math.max(0, parseFloat(editingItem.unitPrice) || 0);

      try {
          await updateDoc(itemRef, {
              name: editingItem.name.trim(),
              unit: editingItem.unit.trim(),
              reorderThreshold: Math.max(0, parseInt(editingItem.reorderThreshold) || 0),
              unitPrice: price,
              lastUpdated: new Date(),
          });
          displayMessage(`Consommable '${editingItem.name}' mis à jour.`);
          setEditingItem(null);
      } catch (e) {
          console.error("Erreur lors de la mise à jour:", e);
          displayMessage("Erreur lors de la mise à jour. Veuillez réessayer.", true);
      }
  }, [db, editingItem, displayMessage, CONSUMABLES_PATH]);

    // Style de bouton de tabulation (inchangé)
  const tabClass = (tabName) =>
      `px-4 py-2 font-semibold text-sm transition-colors rounded-t-lg flex items-center ${
          activeTab === tabName
          ? 'bg-white border-b-2 border-indigo-600 text-indigo-600'
          : 'text-gray-600 hover:bg-gray-100'
      }`;

  // --- Logique de Filtrage ---

  // Filtrage des Consommables (Stock)
  const filteredConsumables = consumables.filter(item => {
    // 1. Recherche par nom
    const matchesSearch = item.name.toLowerCase().includes(stockSearchTerm.toLowerCase());
    
    // 2. Filtrage par statut
    const needsReorder = item.currentStock <= item.reorderThreshold;
    
    if (stockFilterStatus === 'alert') {
      return needsReorder && matchesSearch;
    }
    if (stockFilterStatus === 'ok') {
      return !needsReorder && matchesSearch;
    }
    // Si 'all'
    return matchesSearch;
  });

  // Filtrage des Mouvements (Historique)
  const filteredRecords = records.filter(record => {
    // 1. Filtrage par article
    const matchesItem = historyFilterItem === 'all' || record.itemId === historyFilterItem;
    
    // 2. Filtrage par type
    const matchesType = historyFilterType === 'all' || record.type === historyFilterType;
    
    return matchesItem && matchesType;
  });


  // --- Rendu Principal ---
  if (!db || !isAuthReady || loading) {
      return (
          <div className="flex justify-center items-center h-96">
              <Loader2 className="animate-spin text-indigo-600" size={32} />
              <p className="ml-3 text-gray-600">
                  {db && isAuthReady ? "Chargement des données de stock..." : "Initialisation des services..."}
              </p>
          </div>
      );
  }

  return (
    <div className="min-h-screen font-sans bg-gray-100">
        <div className="bg-white shadow-lg border-b border-indigo-100 py-6 px-4 sm:px-6 md:px-8">
            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center max-w-7xl mx-auto">
                <Package size={30} className="mr-3 text-indigo-600" /> Gestion des Consommables (Coûts Inclus)
            </h1>
        </div>
        <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto">
          {/* Messages Flash */}
          {error && <FlashMessage type="error" text={error} />}
          {message && <FlashMessage type="success" text={message} />}

          {/* Barre de Navigation/Onglets */}
          <div className="border-b border-gray-200 mb-6 bg-white rounded-t-xl shadow-sm">
              <nav className="-mb-px flex space-x-4 px-4 sm:px-6">
                  <button onClick={() => setActiveTab('stock')} className={tabClass('stock')}>
                      <Box size={20} className="mr-2" /> Stock Actuel & Alertes
                  </button>
                  <button onClick={() => setActiveTab('move')} className={tabClass('move')}>
                      <List size={20} className="mr-2" /> Enregistrer Mouvement
                  </button>
                  <button onClick={() => setActiveTab('add')} className={tabClass('add')}>
                      <Plus size={20} className="mr-2" /> Ajouter un Article
                  </button>
                  <button onClick={() => setActiveTab('history')} className={tabClass('history')}>
                      <History size={20} className="mr-2" /> Historique
                  </button>
              </nav>
          </div>

          {/* Contenu des Onglets */}
          <div className="bg-white p-4 sm:p-6 rounded-b-xl shadow-lg">
              {activeTab === 'stock' && (
                <StockList
                  consumables={filteredConsumables} // UTILISE LA LISTE FILTRÉE
                  editingItem={editingItem}
                  startEdit={startEdit}
                  handleSaveEdit={handleSaveEdit}
                  setEditingItem={setEditingItem}
                  formatCurrency={formatCurrency}
                  handleDeleteItem={handleDeleteItem} // PASSE LA FONCTION DE SUPPRESSION
                  stockSearchTerm={stockSearchTerm}
                  setStockSearchTerm={setStockSearchTerm}
                  stockFilterStatus={stockFilterStatus}
                  setStockFilterStatus={setStockFilterStatus}
                />
              )}
              {activeTab === 'add' && (
                <AddItemForm
                  newItem={newItem}
                  setNewItem={setNewItem}
                  handleAddItem={handleAddItem}
                />
              )}
              {activeTab === 'move' && (
                <StockMoveForm
                  consumables={consumables}
                  stockMove={stockMove}
                  setStockMove={setStockMove}
                  handleStockMove={handleStockMove}
                  formatCurrency={formatCurrency}
                  displayMessage={displayMessage}
                />
              )}
              {activeTab === 'history' && (
                <HistoryTable
                  records={filteredRecords} // UTILISE LA LISTE FILTRÉE
                  formatCurrency={formatCurrency}
                  consumables={consumables} // PASSE LES CONSOMMABLES POUR LA LISTE DEROULANTE DE FILTRE
                  historyFilterItem={historyFilterItem}
                  setHistoryFilterItem={setHistoryFilterItem}
                  historyFilterType={historyFilterType}
                  setHistoryFilterType={setHistoryFilterType}
                />
              )}
          </div>
      </div>
  </div>
  );
};

export default ConsumablesManagement;